import { ComponentType, CSSProperties, useEffect, useMemo, useRef } from 'react';
import { first } from 'rxjs/operators';
import { GridApi, ICellRendererParams, IDatasource } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { GridView, LoadIndicatorView } from '@vivli/shared/views';
import { GridCellRendererEnum, LoadIndicatorSizeEnum, TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { IDataRequestSummary } from '@vivli/features/data-requests/infrastructure/interface';
import { DataRequestListTabEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { useAssignedAppTypeContext, useDataRequestsApiContext, useModalApiContext } from '@vivli/core/infrastructure/context';
import { DATA_REQUEST_ROW_HEIGHT, dataRequestGridCustomComponents, useDataRequestGridColumns } from './data-requests-grid-columns.hook';
import { buildDataRequestPageRequest } from './data-request-page-request';

// Rows fetched per request as the user scrolls. Each request costs a few seconds on the server
// (page query plus count), so a block covers several screens of rows.
export const DATA_REQUESTS_BLOCK_SIZE = 50;

const loadingRowStyle: CSSProperties = {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '13px',
    color: '#6b6b6b'
};

const loadingIndicatorStyle: CSSProperties = { paddingTop: 0, height: '22px' };

// Shown in a row whose block is still being fetched while the user scrolls.
const LoadingRowPlaceholder = () => (
    <div style={loadingRowStyle} data-test-id="dataRequestRow_loading">
        <LoadIndicatorView size={LoadIndicatorSizeEnum.Small} style={loadingIndicatorStyle} />
        <span>Loading...</span>
    </div>
);

// The Infinite Row Model renders placeholder rows with no data while a block loads, and the shared
// renderers read data unconditionally. A placeholder row shows the loading indicator in its title
// column and nothing in its other columns.
const withLoadedRow = (Renderer: ComponentType<ICellRendererParams>, Placeholder?: ComponentType) => {
    const LoadedRowRenderer = (props: ICellRendererParams) => {
        if (props.data) {
            return <Renderer {...props} />;
        }

        return Placeholder ? <Placeholder /> : null;
    };
    return LoadedRowRenderer;
};

const pagedCustomComponents = dataRequestGridCustomComponents.map((c) => ({
    ...c,
    component: withLoadedRow(c.component, c.name === GridCellRendererEnum.DataRequestTitle ? LoadingRowPlaceholder : undefined)
}));

interface PagedDataRequestsGridComponentProps {
    assignedAppType: string;
    tab: DataRequestListTabEnum;
    search?: string;
    onRowClick?: (dataRequest: IDataRequestSummary) => void;
    onGridReady?: (gridApi: GridApi) => void;
    onTotalCountChange?: (count: number) => void;
    emptyMessage?: string;
    isPresumedResearcher?: boolean;
    initialSortField?: string;
    initialSortOrder?: 'asc' | 'desc';
}

// DEV-1003: the admin Data Requests grid, paged on the server. Same columns as
// DataRequestsGridComponent, but rows of one tab load in blocks as the user scrolls, through ag-grid's
// Infinite Row Model, and the tab's count comes from the server's total.
// DEV-958: the column sorts and floating filters are sent to the server with each block request.
export const PagedDataRequestsGridComponent = ({
    assignedAppType,
    tab,
    search,
    onRowClick,
    onGridReady,
    onTotalCountChange,
    emptyMessage,
    isPresumedResearcher,
    initialSortField,
    initialSortOrder
}: PagedDataRequestsGridComponentProps) => {
    const dataRequestsApi = useDataRequestsApiContext();
    const modalService = useModalApiContext();
    const { isAmr } = useAssignedAppTypeContext();
    const { t } = useTranslation();
    const duaTranslation = t(TranslationKeyEnum.duaValidatedStatusString);
    const gridApiRef = useRef<GridApi>();
    const onTotalCountChangeRef = useRef(onTotalCountChange);
    const activeDatasourceRef = useRef<IDatasource>(null);

    useEffect(() => {
        onTotalCountChangeRef.current = onTotalCountChange;
    }, [onTotalCountChange]);

    const columns = useDataRequestGridColumns({ isPresumedResearcher, initialSortField, initialSortOrder });

    // A new datasource makes ag-grid drop its cache and start again from the first block,
    // so a tab or search change needs nothing more than this memo. A sort or filter change stays
    // inside the datasource: ag-grid passes the current models with every block request.
    const datasource = useMemo<IDatasource>(() => {
        const thisDatasource: IDatasource = {
            getRows: (params) => {
                const isActive = () => activeDatasourceRef.current === thisDatasource;
                const showCount = (totalCount: number) => {
                    onTotalCountChangeRef.current?.(totalCount);
                    if (totalCount === 0) {
                        gridApiRef.current?.showNoRowsOverlay();
                    } else {
                        gridApiRef.current?.hideOverlay();
                    }
                };

                const request = buildDataRequestPageRequest({
                    tab,
                    startRow: params.startRow,
                    endRow: params.endRow,
                    search,
                    sortModel: params.sortModel,
                    filterModel: params.filterModel,
                    isAmr,
                    duaTranslation
                });

                // a Status filter that matches no status text can match no row; answer without a request
                if (!request) {
                    params.successCallback([], 0);
                    if (isActive()) {
                        showCount(0);
                    }
                    return;
                }

                dataRequestsApi
                    .getDataRequestSummaryPage(assignedAppType, request)
                    .pipe(first())
                    .subscribe({
                        next: (page) => {
                            // ag-grid discards rows for a datasource it has replaced; do the same
                            // with the count and overlay, or a slow reply from an old search can
                            // overwrite the badge and cover rows that did load
                            params.successCallback(page.rows, page.totalCount);
                            if (isActive()) {
                                showCount(page.totalCount);
                            }
                        },
                        error: (error) => {
                            params.failCallback();
                            modalService.error(`An error happened getting Data Requests. Error received: ${error}`);
                        }
                    });
            }
        };
        return thisDatasource;
    }, [assignedAppType, tab, search, isAmr, duaTranslation]);

    useEffect(() => {
        activeDatasourceRef.current = datasource;
    }, [datasource]);

    const handleGridReady = (gridApi: GridApi) => {
        gridApiRef.current = gridApi;
        onGridReady && onGridReady(gridApi);
    };

    return (
        <GridView
            rowModelType="infinite"
            datasource={datasource}
            cacheBlockSize={DATA_REQUESTS_BLOCK_SIZE}
            getRowId={(params) => params.data.id}
            columns={columns}
            fullWidthColumns={true}
            customComponents={pagedCustomComponents}
            rowHeight={DATA_REQUEST_ROW_HEIGHT}
            // a placeholder row that is still loading has no data to open
            onRowClick={(dataRequest: IDataRequestSummary) => dataRequest && onRowClick && onRowClick(dataRequest)}
            onGridReady={handleGridReady}
            emptyMessage={emptyMessage}
        />
    );
};
